import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import { useGetAnalysisSummaryByDataset } from "@/api/analysis/useAnalysisAPI";
import Dropdown from "@/components/ui/dropdown";
import { IOptionItem } from "@/components/ui/dropdown/types";
import DropdownMultiSelect from "@/components/ui/dropdownMultiSelect";
import { useAnalysisSummaryDataContext } from "@/contexts/AnalysisSummaryDataContext";
import { useDebounce } from "@/hooks/useDebounce";
import { RootState } from "@/store";
import { DatasetData, DatasetDataRowV2 } from "@/types/dataset";

import { SORT_OPTIONS } from "./constants";
import { AnalysisConfiguration } from "./types";
import { getDatasetSummarySortByOptions, getFilteredGrids } from "./utils";

const getRowOption = (row: DatasetDataRowV2) => {
  const candidate = row[0];
  const value = candidate ? String(candidate) : "";
  const label = value || "null";
  return { label, value };
};

type SortAndFilterSectionProps = {
  dataset: DatasetData;
  gridsSelected: string[];
  analysisConfiguration: AnalysisConfiguration | null;
  filterByOptions: IOptionItem[];
  setFilterByOptions: React.Dispatch<React.SetStateAction<IOptionItem[]>>;
  setAnalysisConfiguration: (method: AnalysisConfiguration | null) => void;
};

const SortAndFilterSection = ({
  dataset,
  filterByOptions,
  setFilterByOptions,
  analysisConfiguration,
  setAnalysisConfiguration
}: SortAndFilterSectionProps) => {
  const { latestAnalysisId } = useSelector((state: RootState) => state.spatial);
  const { gridsSelected } = useSelector(
    (state: RootState) => state.analysisSummary
  );
  const { gridValue } = useAnalysisSummaryDataContext();
  const debouncedGridsSelected = useDebounce(gridsSelected, 500);

  const sortByOptions = useMemo(
    () =>
      getDatasetSummarySortByOptions(
        analysisConfiguration,
        debouncedGridsSelected,
        gridValue
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      analysisConfiguration?.calculationMethod?.value,
      analysisConfiguration?.method,
      analysisConfiguration?.category,
      debouncedGridsSelected
    ]
  );

  const filteredGrids = getFilteredGrids(
    analysisConfiguration?.category?.value,
    debouncedGridsSelected,
    gridValue
  );

  const { data: summaryData } = useGetAnalysisSummaryByDataset(
    dataset.dataset_id,
    latestAnalysisId,
    { ...analysisConfiguration, filterBy: undefined },
    filteredGrids
  );

  useEffect(() => {
    if (
      sortByOptions.some(
        (item) => item.value === analysisConfiguration?.sortBy?.value
      )
    )
      return;

    setAnalysisConfiguration({
      ...analysisConfiguration,
      sortBy: sortByOptions?.[0]
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortByOptions]);

  useEffect(() => {
    if (!summaryData) return;
    const options: IOptionItem[] = summaryData.rows.map(getRowOption);

    setFilterByOptions(options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryData]);

  return (
    <>
      <DropdownMultiSelect
        className="w-full mt-2"
        placeholder="Filter By"
        name="filterBy"
        data={filterByOptions}
        showSelectAll
        onSelect={(option) =>
          setAnalysisConfiguration({
            ...analysisConfiguration,
            filterBy: option
          })
        }
        defaultValue={analysisConfiguration?.filterBy || []}
      />
      <Dropdown
        className="w-full mt-2"
        name="sort"
        placeholder="Sort"
        data={SORT_OPTIONS}
        onSelect={(option) =>
          setAnalysisConfiguration({ ...analysisConfiguration, sort: option })
        }
        value={analysisConfiguration?.sort}
      />
      <Dropdown
        className="w-full mt-2"
        name="sortBy"
        placeholder="Sort by"
        data={sortByOptions}
        onSelect={(option) =>
          setAnalysisConfiguration({ ...analysisConfiguration, sortBy: option })
        }
        value={analysisConfiguration?.sortBy}
      />
    </>
  );
};

export default SortAndFilterSection;
